// The cars list's query string, in one place (spec: cars-list-filters-in-url).
//
// The URL is the ONLY place the filters and the page live. This module is the whole contract between
// the page and that URL: what the keys are, what counts as a value, and what the API is sent. Pure on
// purpose — no React, no antd, no `src/config` — so it is proved with the bare jest react-scripts
// ships, and so the next list can adopt it.

export const DEFAULT_PAGE_SIZE = 50
export const PAGE_SIZES = [50, 100, 200]

// The query keys, in the order the chips are drawn.
export const FILTER_KEYS = [
  'q',
  'make',
  'model',
  'minYear',
  'maxYear',
  'minPrice',
  'maxPrice',
  'maxMileage',
  'condition',
  'origin',
  'status',
  'posted'
]

const NUMBER_KEYS = ['minYear', 'maxYear', 'minPrice', 'maxPrice', 'maxMileage']

// What the API's POST body calls each filter.
const BODY_NAMES = {
  q: 'q',
  make: 'make',
  model: 'model',
  minYear: 'min_year',
  maxYear: 'max_year',
  minPrice: 'min_price',
  maxPrice: 'max_price',
  maxMileage: 'max_mileage',
  condition: 'condition',
  origin: 'origin',
  status: 'status',
  posted: 'posted_on_social_media'
}

const clean = value => (value === undefined || value === null ? '' : String(value).trim())

// A query string → { filters, page, pageSize }. Garbage parses to defaults: a page that is not a
// number is page 1, a `posted` that is not true/false is absent, a key we do not know is ignored.
export const parseQuery = search => {
  const params = new URLSearchParams(search || '')
  const filters = {}
  FILTER_KEYS.forEach(key => {
    const value = clean(params.get(key))
    if (!value) return
    if (NUMBER_KEYS.includes(key)) {
      if (!/^\d+(\.\d+)?$/.test(value)) return
      filters[key] = value
      return
    }
    if (key === 'posted') {
      if (value === 'true' || value === 'false') filters.posted = value
      return
    }
    filters[key] = value
  })
  const page = Math.max(1, parseInt(params.get('page'), 10) || 1)
  const requestedSize = parseInt(params.get('pageSize'), 10)
  const pageSize = PAGE_SIZES.includes(requestedSize) ? requestedSize : DEFAULT_PAGE_SIZE
  return { filters, page, pageSize }
}

// { filters, page, pageSize } → "?make=abc&page=3". Nothing empty is written; page 1 and the default
// page size are omitted, so `/cars` with no query is exactly "all cars, page 1".
export const buildQuery = (filters = {}, page = 1, pageSize = DEFAULT_PAGE_SIZE) => {
  const params = new URLSearchParams()
  FILTER_KEYS.forEach(key => {
    const value = clean(filters[key])
    if (value) params.set(key, value)
  })
  if (page && page > 1) params.set('page', String(page))
  if (pageSize && pageSize !== DEFAULT_PAGE_SIZE && PAGE_SIZES.includes(pageSize)) {
    params.set('pageSize', String(pageSize))
  }
  const text = params.toString()
  return text ? `?${text}` : ''
}

export const withPage = (search, page) => {
  const { filters, pageSize } = parseQuery(search)
  return buildQuery(filters, page, pageSize)
}

export const withPageSize = (search, pageSize) => {
  const { filters } = parseQuery(search)
  return buildQuery(filters, 1, pageSize)
}

// One chip removed. Dropping the make drops the model too: a model belongs to a make.
export const withoutFilter = (search, key) => {
  const { filters, pageSize } = parseQuery(search)
  const next = { ...filters }
  delete next[key]
  if (key === 'make') delete next.model
  return buildQuery(next, 1, pageSize)
}

// The POST body the search endpoint reads. Only the filters that are set.
export const toSearchBody = filters => {
  const body = {}
  Object.keys(filters || {}).forEach(key => {
    const name = BODY_NAMES[key]
    const value = clean(filters[key])
    if (name && value) body[name] = value
  })
  return body
}

export const activeCount = filters => Object.keys(toSearchBody(filters)).length

// What a chip says. Ids are turned into names through the option lists the page has loaded; until a
// list is loaded the chip shows "…" rather than an id (P6).
export const chipLabel = (key, value, options = {}) => {
  const named = (list, fallback) => {
    if (!list) return '…'
    const hit = list.find(o => String(o.value) === String(value))
    return hit ? hit.label : fallback
  }
  switch (key) {
    case 'q':
      return `“${value}”`
    case 'make':
      return named(options.makes, '…')
    case 'model':
      return named(options.models, '…')
    case 'condition':
      return named(options.conditions, '…')
    case 'origin':
      return named(options.origins, '…')
    case 'status':
      return named(options.statuses, '…')
    case 'minYear':
      return `From ${value}`
    case 'maxYear':
      return `To ${value}`
    case 'minPrice':
      return `Price ≥ ${Number(value).toLocaleString()}`
    case 'maxPrice':
      return `Price ≤ ${Number(value).toLocaleString()}`
    case 'maxMileage':
      return `Mileage ≤ ${Number(value).toLocaleString()}`
    case 'posted':
      return value === 'true' ? 'Posted' : 'Not posted'
    default:
      return String(value)
  }
}
